import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { storableError } from '../../util/errors';
import { parse } from '../../util/urlHelpers';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

// A bundle order's row shows up to this many book covers, then "+N".
export const BUNDLE_COVER_LIMIT = 3;

// The listings in a bundle order, in basket order: the first is the order's
// main listing. Stored on the order at checkout.
export const bundleListingIds = protectedData =>
  protectedData?.isCartPurchase && protectedData?.cart ? Object.keys(protectedData.cart) : [];

const COVER_IMAGE_VARIANTS = ['variants.square-small', 'variants.square-small2x'];

// The main listing's cover comes with the orders. The other covers of every
// bundle on the page are fetched in one query. Best effort: if it fails, a
// bundle row just shows its main cover.
const fetchBundleCovers = (response, sdk, dispatch) => {
  const ids = new Set();
  response.data.data.forEach(tx => {
    const mainId = tx.relationships?.listing?.data?.id?.uuid;
    bundleListingIds(tx.attributes?.protectedData)
      .slice(0, BUNDLE_COVER_LIMIT)
      .forEach(id => {
        if (id !== mainId) {
          ids.add(id);
        }
      });
  });
  if (ids.size === 0) {
    return Promise.resolve();
  }
  return sdk.listings
    .query({
      ids: [...ids].slice(0, 100),
      include: ['images'],
      'fields.listing': ['title'],
      'fields.image': COVER_IMAGE_VARIANTS,
      'limit.images': 1,
    })
    .then(coversResponse => dispatch(addMarketplaceEntities(coversResponse)))
    .catch(() => null);
};

// ================ Action types ================ //

export const FETCH_ORDERS_OR_SALES_REQUEST = 'app/InboxPage/FETCH_ORDERS_OR_SALES_REQUEST';
export const FETCH_ORDERS_OR_SALES_SUCCESS = 'app/InboxPage/FETCH_ORDERS_OR_SALES_SUCCESS';
export const FETCH_ORDERS_OR_SALES_ERROR = 'app/InboxPage/FETCH_ORDERS_OR_SALES_ERROR';

// ================ Reducer ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  pagination: null,
  transactionRefs: [],
};

export default function inboxPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ORDERS_OR_SALES_REQUEST:
      return { ...state, fetchInProgress: true, fetchOrdersOrSalesError: null };
    case FETCH_ORDERS_OR_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_ORDERS_OR_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchInProgress: false, fetchOrdersOrSalesError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchOrdersOrSalesRequest = () => ({ type: FETCH_ORDERS_OR_SALES_REQUEST });
const fetchOrdersOrSalesSuccess = response => ({
  type: FETCH_ORDERS_OR_SALES_SUCCESS,
  payload: response,
});
const fetchOrdersOrSalesError = e => ({
  type: FETCH_ORDERS_OR_SALES_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const INBOX_PAGE_SIZE = 10;

export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const { tab } = params;

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for InboxPage: ${tab}`));
  }

  dispatch(fetchOrdersOrSalesRequest());

  // Unread markers come from the current user's profile, so refresh it to pick
  // up messages that arrived since it was last loaded. One request, not one
  // per conversation.
  dispatch(fetchCurrentUser({ updateHasListings: false })).catch(() => {
    // The inbox still works with the profile already in the store.
  });

  const { page = 1 } = parse(search);

  const apiQueryParams = {
    only: onlyFilter,
    lastTransitions: getAllTransitionsForEveryProcess(),
    include: [
      'listing',
      'listing.images',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'protectedData',
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
    'fields.image': COVER_IMAGE_VARIANTS,
    'limit.images': 1,
    page,
    perPage: INBOX_PAGE_SIZE,
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchOrdersOrSalesSuccess(response));
      return fetchBundleCovers(response, sdk, dispatch).then(() => response);
    })
    .catch(e => {
      dispatch(fetchOrdersOrSalesError(storableError(e)));
      throw e;
    });
};
